import { useEffect, useId, useMemo, useState } from 'react';
import { Button, Header, Icon, Message, Segment, Table } from 'semantic-ui-react';
import { describe } from '../approvals.js';
import { DEFAULT_STATUS, STATUS_OPTIONS, buildHome, passesStatus, statusById } from '../home.js';
import { MONTH_NAMES } from '../periods.js';
import StatusSelect from './StatusSelect.jsx';
import { buildSummary, columnsWithValues, filterRows, fmt, latestPerReport, totalsOf } from '../summary.js';

const NONE = [];                                              // a stable default, so a missing prop does not re-run the memos and effects
const NO_FILTER = { partner: '', reportId: '', month: 'all' };

const DECEMBER = 11;
const COLLAPSE_KEY = 'srs_summary_collapsed';
const savedCollapsed = () => { try { return window.localStorage.getItem(COLLAPSE_KEY) === 'true'; } catch { return false; } };

/* Every report as one row per month of the chosen year (months with no figures are left out), with the line items (sites, sockets,
 * financials) as columns. Pick the year, then narrow by partner, report or month. Open to everyone, whoever the account manager is. */
export default function SummaryTab({ reports, config = NONE, approvals = NONE, loadFull, onExcel, busy, today: todayProp }) {
  const today = useMemo(() => todayProp || new Date(), [todayProp]);   // stable between renders: it feeds memos and effects
  const years = useMemo(() => [...new Set(reports.map((r) => r.report_year))].sort((a, b) => b - a), [reports]);
  const [year, setYear] = useState(null);
  const [filters, setFilters] = useState(NO_FILTER);
  const [status, setStatus] = useState(DEFAULT_STATUS);          // the Home page's Status filter: starts on Up to date
  const [entries, setEntries] = useState(null);                    // [{ report, figures }] for the chosen year
  const [error, setError] = useState(null);
  const [collapsed, setCollapsed] = useState(savedCollapsed);   // heading and filters folded away, to leave more room for the table
  const uid = useId();
  const toggle = () => setCollapsed((c) => { try { window.localStorage.setItem(COLLAPSE_KEY, String(!c)); } catch { /* storage blocked */ } return !c; });

  const y = year ?? years[0] ?? null;
  const homeStatus = useMemo(() => (y === null ? new Map() : statusById(buildHome(reports, approvals, today, y, config))), [reports, approvals, today, y, config]);
  const picked = useMemo(() => (y === null ? [] : latestPerReport(reports, y).filter((r) => passesStatus(homeStatus, r.settlement_report_id, status))), [reports, y, homeStatus, status]);
  const m = DECEMBER;                                          // the whole year: months a report has no figures for simply have no row

  useEffect(() => {
    let live = true;
    setEntries(null); setError(null);
    Promise.all(picked.map((r) => loadFull(r))).then((all) => { if (live) setEntries(all); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [picked, loadFull]);

  const summary = useMemo(() => (entries && y !== null ? buildSummary(entries, y, m, approvals) : null), [entries, y, m, approvals]);
  const only = filters.month;
  const shown = useMemo(() => (summary ? filterRows(summary.rows, { ...filters, month: only }) : []), [summary, filters, only]);
  const { columns, groups } = useMemo(() => (summary ? columnsWithValues(summary.columns, shown) : { columns: [], groups: [] }), [summary, shown]);   // no column for a line item with no figures in the rows shown
  const totals = useMemo(() => (summary ? totalsOf(columns, shown) : {}), [summary, columns, shown]);
  const partners = useMemo(() => [...new Set((summary?.rows || []).map((r) => r.partner).filter(Boolean))].sort(), [summary]);
  const ids = useMemo(() => [...new Set((summary?.rows || []).map((r) => r.id))].sort(), [summary]);
  const filtered = !!(filters.partner.trim() || filters.reportId.trim() || only !== 'all' || status !== DEFAULT_STATUS);
  const set = (patch) => setFilters((f) => ({ ...f, ...patch }));

  return (
    <section aria-labelledby="sum-heading">
      <div className={`sumhead${collapsed ? ' collapsed' : ''}`}>
        {collapsed ? (
          <h2 id="sum-heading" className="ui small header sumtitle"><Icon name="table" />Summary</h2>
        ) : (
          <Header as="h2" id="sum-heading" className="titlebar">
            <Icon name="table" />
            <Header.Content>Summary<Header.Subheader>Every report, month by month for the year; by-site figures are summed over the sites</Header.Subheader></Header.Content>
          </Header>
        )}
        <div className="toolbar" role="group" aria-label="Choose the summary year">
          <label htmlFor="sum-year">Year</label>
          <select id="sum-year" className="ui dropdown" value={y ?? ''} onChange={(e) => { setYear(Number(e.target.value)); setFilters((f) => ({ ...f, month: 'all' })); }}>
            {years.map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <Button type="button" icon="file excel outline" labelPosition="left" content="Download to Excel" loading={busy === 'summary'}
            disabled={!summary || !shown.length || !!busy} onClick={() => onExcel({ ...summary, columns, groups, rows: shown, totals, filters: { ...filters, month: only, statusLabel: (STATUS_OPTIONS.find(([v]) => v === status) || [])[1] } })} />
          {collapsed && summary && summary.rows.length > 0 && <span className="hint" role="status">Showing {shown.length} of {summary.rows.length} rows{filtered ? ' (filtered)' : ''}</span>}
          <Button type="button" basic compact size="small" className="sumtoggle" icon={collapsed ? 'angle double down' : 'angle double up'} labelPosition="left"
            content={collapsed ? 'Show heading and filters' : 'Collapse heading'} aria-expanded={!collapsed} aria-controls="sum-filters" onClick={toggle} />
        </div>
      </div>
      <div id="sum-filters" hidden={collapsed}>
        <div className="toolbar filterbar" role="search" aria-label="Filter the summary">
          <span className="fgroup">
            <label htmlFor={`${uid}-partner`}>Partner</label>
            <input id={`${uid}-partner`} type="search" className="filterinput" list={`${uid}-partners`} placeholder="Any partner" autoComplete="off"
              value={filters.partner} onChange={(e) => set({ partner: e.target.value })} />
            <datalist id={`${uid}-partners`}>{partners.map((p) => <option key={p} value={p} />)}</datalist>
          </span>
          <span className="fgroup">
            <label htmlFor={`${uid}-report`}>Report ID</label>
            <input id={`${uid}-report`} type="search" className="filterinput short" list={`${uid}-ids`} placeholder="Any report" autoComplete="off"
              value={filters.reportId} onChange={(e) => set({ reportId: e.target.value })} />
            <datalist id={`${uid}-ids`}>{ids.map((p) => <option key={p} value={p} />)}</datalist>
          </span>
          <span className="fgroup">
            <label htmlFor={`${uid}-only`}>Only month</label>
            <select id={`${uid}-only`} className="ui dropdown" value={only} onChange={(e) => set({ month: e.target.value })}>
              <option value="all">All months</option>
              {MONTH_NAMES.map((n, i) => <option key={n} value={i}>{n}</option>)}
            </select>
          </span>
          <span className="fgroup"><StatusSelect id={`${uid}-status`} value={status} onChange={setStatus} /></span>
          {filtered && <Button type="button" basic compact size="small" icon="close" labelPosition="left" content="Clear filters" onClick={() => { setFilters(NO_FILTER); setStatus(DEFAULT_STATUS); }} />}
        </div>
      </div>
      {error && <Message negative role="alert">{error}</Message>}
      {!error && !summary && y !== null && <Message info role="status">Loading reports…</Message>}
      {y === null && <Segment placeholder><Header icon><Icon name="folder open outline" />There are no reports yet.</Header></Segment>}
      {summary && summary.rows.length === 0 && <Message info>{status ? `No ${STATUS_OPTIONS.find(([v]) => v === status)[1].toLowerCase()} reports with figures for ${y}. Change the Status filter to see others.` : `No figures for ${y}.`}</Message>}
      {summary && summary.rows.length > 0 && (
        <>
          <p className="hint rowcount" role="status" hidden={collapsed}>Showing {shown.length} of {summary.rows.length} rows. Months in which a report has no figures yet are left out.</p>
          {shown.length === 0 ? <Message info>No rows match the filters.</Message> : (
            <div className={`gridwrap sumwrap${collapsed ? ' tall' : ''}`} tabIndex={0} role="region" aria-label={`${summary.title} (scrollable)`}>
              <Table celled compact unstackable className="datagrid sumtable">
                <caption className="visually-hidden">{summary.title}. One row per report and month; one column per line item. By-site figures are summed over the sites.</caption>
                <Table.Header>
                  <Table.Row className="grouprow">
                    <Table.HeaderCell scope="col" rowSpan={2} className="lab c1">Report</Table.HeaderCell>
                    <Table.HeaderCell scope="col" rowSpan={2} className="c2">Month</Table.HeaderCell>
                    {groups.map((g) => <Table.HeaderCell scope="colgroup" key={g.section} colSpan={g.span} className={`grp ${g.section}`}>{g.title}</Table.HeaderCell>)}
                    <Table.HeaderCell scope="colgroup" colSpan={3} className="grp status">Owner &amp; approval</Table.HeaderCell>
                  </Table.Row>
                  <Table.Row>
                    {columns.map((c) => <Table.HeaderCell scope="col" key={c.key} textAlign="center" className="metric">{c.label}</Table.HeaderCell>)}
                    <Table.HeaderCell scope="col" className="metric">Account manager</Table.HeaderCell>
                    <Table.HeaderCell scope="col" className="metric">Finance approval</Table.HeaderCell>
                    <Table.HeaderCell scope="col" className="metric">AM approval</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {shown.map((r, i) => (
                    <Table.Row key={r.key} className={i === 0 || shown[i - 1].reportKey !== r.reportKey ? 'newgroup' : undefined}>
                      <Table.HeaderCell scope="row" className="lab c1" title={`${r.id} – ${r.partner}`}>{r.id}<div className="sub">{r.partner}</div></Table.HeaderCell>
                      <Table.Cell className="c2">{r.month}</Table.Cell>
                      {columns.map((c) => <Table.Cell key={c.key} textAlign="right">{fmt(c.kind, r.values[c.key]) || '–'}</Table.Cell>)}
                      <Table.Cell>{r.manager || '–'}</Table.Cell>
                      <Table.Cell className="approvalcell">{describe(r.finance) || <span className="hint">Pending</span>}</Table.Cell>
                      <Table.Cell className="approvalcell">{describe(r.account_manager) || <span className="hint">Pending</span>}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
                <Table.Footer>
                  <Table.Row className="totalrow">
                    <Table.HeaderCell scope="row" className="lab c1">Total ({shown.length} row{shown.length === 1 ? '' : 's'})</Table.HeaderCell>
                    <Table.HeaderCell className="c2" />
                    {columns.map((c) => <Table.HeaderCell key={c.key} textAlign="right">{fmt(c.kind, totals[c.key]) || '–'}</Table.HeaderCell>)}
                    <Table.HeaderCell colSpan={3} />
                  </Table.Row>
                </Table.Footer>
              </Table>
            </div>
          )}
          <p className="hint">Totals add up the rows shown. Percentages are never added, and site / chargepoint / socket counts are added only when the rows are all one month.</p>
        </>
      )}
    </section>
  );
}
